import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts"
import {
    CreateMembershipFeeTypeRequest,
    MembershipFeeType,
    UpdateMembershipFeeTypeRequest
} from '@/models/membership-fee'

// Re-export the type for convenience
export type {MembershipFeeType} from '@/models/membership-fee'

const NBH_MEMBERSHIP_FEE_PUBLIC_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/membership-fees`
const NBH_MEMBERSHIP_FEE_ADMIN_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/membership-fees`

function getAuthHeaders(token: string | null): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        ...(token && {'Authorization': `Bearer ${token}`})
    }
}

interface BackendFeeVersion {
    id: string
    versionNumber: number
    amount: { parsedValue: number } | number
    validFrom: [number, number, number] | string
    validUntil?: string
    isActive: boolean
    createdAt: string
    createdBy: string
}

interface BackendFeeType {
    id: string
    name: string
    description?: string
    isDefault: boolean
    currentAmount?: number
    versions: BackendFeeVersion[]
    canBeDeleted: boolean
    createdAt: string
    createdBy: string
    updatedAt?: string
    updatedBy?: string
}

function transformFeeType(feeType: BackendFeeType): MembershipFeeType {
    // Transform versions first
    const transformedVersions = (feeType.versions || []).map((v: BackendFeeVersion) => ({
        ...v,
        amount: typeof v.amount === 'object' ? v.amount.parsedValue : v.amount,
        validFrom: Array.isArray(v.validFrom)
            ? `${v.validFrom[0]}-${String(v.validFrom[1]).padStart(2, '0')}-${String(v.validFrom[2]).padStart(2, '0')}`
            : v.validFrom
    }))

    // Calculate currentAmount: use backend value if available, otherwise find active version
    let currentAmount = feeType.currentAmount
    if (currentAmount === undefined && transformedVersions.length > 0) {
        const activeVersion = transformedVersions.find(v => v.isActive)
        currentAmount = activeVersion?.amount
    }

    return {
        ...feeType,
        currentAmount,
        versions: transformedVersions
    }
}

export async function getMembershipFeeTypes(token: string | null): Promise<MembershipFeeType[]> {
    if (!token) {
        return Promise.reject("No token provided")
    }

    try {
        const response = await fetch(NBH_MEMBERSHIP_FEE_PUBLIC_URL, {
            method: 'GET',
            headers: getAuthHeaders(token)
        })

        if (response.status === 404) {
            return []
        }

        const result = await handleResponse<BackendFeeType[] | Record<string, BackendFeeType>>(response)
        if (!result) return []
        
        // Transform the response to handle both array and object formats
        const feeTypesArray = Array.isArray(result) ? result : Object.values(result)
        
        // Process each fee type using the transform function
        return feeTypesArray.map(transformFeeType)
    } catch (error: any) {
        if (error.status === 404) {
            return []
        }
        throw error
    }
}

export async function createMembershipFeeType(
    token: string | null,
    request: CreateMembershipFeeTypeRequest
): Promise<MembershipFeeType> {
    if (!token) {
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(NBH_MEMBERSHIP_FEE_ADMIN_URL, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(request)
    })

    const result = await handleResponse<BackendFeeType>(response)
    if (!result) throw new Error('No data returned')
    return transformFeeType(result)
}

export async function updateMembershipFeeType(
    token: string | null,
    id: string,
    request: UpdateMembershipFeeTypeRequest
): Promise<MembershipFeeType> {
    if (!token) {
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_MEMBERSHIP_FEE_ADMIN_URL}/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(request)
    })

    const result = await handleResponse<BackendFeeType>(response)
    if (!result) throw new Error('No data returned')
    return transformFeeType(result)
}

export async function deleteMembershipFeeType(token: string | null, id: string): Promise<void> {
    if (!token) {
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_MEMBERSHIP_FEE_ADMIN_URL}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
    })

    if (!response.ok) {
        const error = await response.text()
        throw new Error(error || 'Failed to delete membership fee type')
    }
}

export async function setDefaultMembershipFeeType(token: string | null, id: string): Promise<MembershipFeeType> {
    if (!token) {
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_MEMBERSHIP_FEE_ADMIN_URL}/${id}/default`, {
        method: 'PUT',
        headers: getAuthHeaders(token)
    })

    const result = await handleResponse<BackendFeeType>(response)
    if (!result) throw new Error('No data returned')
    return transformFeeType(result)
}

// Member fee assignment functions
export interface MemberFeeAssignment {
    id: string
    memberId: string
    feeTypeId: string
    feeTypeName: string
    feeTypeAnnualAmount: number
    validFrom: string
    validUntil?: string
    createdAt: string
    createdBy: string
    updatedAt?: string
    updatedBy?: string
}

export interface AssignFeeTypeRequest {
    memberId: string
    feeTypeId: string
    validFrom: string
    validUntil?: string
}

// Convenience function for assigning fee type with simplified parameters
export async function assignMembershipFeeType(
    token: string | null,
    request: AssignFeeTypeRequest
): Promise<MemberFeeAssignment> {
    return assignFeeTypeToMember(token, request.memberId, {
        feeTypeId: request.feeTypeId,
        validFrom: request.validFrom,
        validUntil: request.validUntil
    })
}

export async function assignFeeTypeToMember(
    token: string | null,
    memberId: string,
    request: Omit<AssignFeeTypeRequest, 'memberId'>
): Promise<MemberFeeAssignment> {
    if (!token) {
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/member-fees/${memberId}/assignments`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(request)
    })

    const result = await handleResponse<MemberFeeAssignment>(response)
    if (!result) throw new Error('No data returned')
    return result
}

export async function getCurrentMemberFeeAssignment(
    token: string | null,
    memberId: string
): Promise<MemberFeeAssignment | null> {
    if (!token) {
        return Promise.reject("No token provided")
    }

    const url = `${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/member-fees/${memberId}/assignments/current`;

    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(token)
    })

    if (response.status === 204 || !response.ok) {
        return null
    }

    try {
        const text = await response.text();

        if (!text || text === 'null') {
            return null;
        }

        const result = JSON.parse(text);

        // Backend returns null if no assignment exists
        return result || null
    } catch (_error) {
        return null
    }
}

export async function getMemberFeeAssignmentHistory(
    token: string | null,
    memberId: string
): Promise<MemberFeeAssignment[]> {
    if (!token) {
        return Promise.reject("No token provided")
    }

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/member-fees/${memberId}/assignments/history`, {
        method: 'GET',
        headers: getAuthHeaders(token)
    })

    const result = await handleResponse<MemberFeeAssignment[]>(response)
    return result || []
}

export async function checkOrganizationHasFeeTypes(token: string | null): Promise<boolean> {
    if (!token) {
        return Promise.reject("No token provided")
    }

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/member-fees/dummy/assignments/organization-has-fees`, {
        method: 'GET',
        headers: getAuthHeaders(token)
    })

    const result = await handleResponse<boolean>(response)
    return result || false
}
